import React from 'react';
import {
	Box,
	Checkbox,
	Chip,
	FormHelperText,
	ListItemText,
	MenuItem,
	Select,
	Typography,
	SelectChangeEvent,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { Permission } from 'common/types';
import { PermissionOption, getPermissionText } from '../utils';
import { useTranslation } from 'services/localization/useTranslation';

export interface PermissionUpdate {
	permissions: Permission[];
	restrictions: Permission[];
}

interface Props {
	permissions: Permission[];
	restrictions: Permission[];
	onChange: (value: PermissionUpdate) => void;
	disabled?: boolean;
	helperText?: string;
	permissionOptions: PermissionOption[];
	defaultPermissions: Permission[];
}

const PermissionsSelect = (props: Props) => {
	const {
		permissions,
		restrictions,
		onChange,
		disabled,
		helperText,
		permissionOptions,
		defaultPermissions,
	} = props;
	const classes = useStyles();
	const { t } = useTranslation();

	const handleChange = (e: SelectChangeEvent) => {
		const values = (e.target.value as unknown) as Permission[];
		const restrictions = defaultPermissions.filter((p) => !values.includes(p));
		const permissions = values.filter((p) => !defaultPermissions.includes(p));

		onChange({ permissions, restrictions });
	};

	const selectValue = [
		...permissions,
		...defaultPermissions.filter((dp) => !restrictions.includes(dp)),
	];
	const renderValue = () => {
		if (!permissions.length && !restrictions.length) {
			return (
				<Typography variant="body1" color="secondary" className={classes.emptyText}>
					{t('users.selectPermissions', 'Select permissions')}
				</Typography>
			);
		}
		return [...permissions, ...restrictions].map((perm) => {
			const isRestriction = restrictions.includes(perm);
			return (
				<Chip
					key={perm}
					label={`${isRestriction ? '-' : '+'} ${getPermissionText(perm)}`}
					color={isRestriction ? 'secondary' : 'default'}
				/>
			);
		});
	};

	return (
		<>
			<Select
				multiple
				fullWidth
				displayEmpty
				variant="outlined"
				value={(selectValue as unknown) as string}
				onChange={handleChange}
				renderValue={renderValue}
				disabled={disabled}
				MenuProps={{
					classes: { paper: classes.menuPaper },
					anchorOrigin: {
						vertical: 'bottom',
						horizontal: 'right',
					},
					transformOrigin: {
						vertical: 'top',
						horizontal: 'right',
					},
				}}
			>
				{permissionOptions.map((option) => (
					<MenuItem key={option.value} value={option.value} className={classes.menuItem} divider>
						<Box mr={2}>
							<Checkbox
								color="primary"
								checked={selectValue.includes(option.value)}
								className={classes.menuItemCheckbox}
							/>
						</Box>

						<ListItemText
							disableTypography
							primary={
								<Box display="flex" justifyContent="space-between" mb={0.5}>
									<Typography variant="body1">{option.label}</Typography>
									{defaultPermissions.includes(option.value) && (
										<Chip
											size="small"
											color="secondary"
											label={t('users.includedForRole', 'Included for role')}
										></Chip>
									)}
								</Box>
							}
							secondary={
								<Typography variant="body2" color="secondary">
									{option.description}
								</Typography>
							}
						/>
					</MenuItem>
				))}
			</Select>
			{helperText && (
				<Box mx={1.75}>
					<FormHelperText>{helperText}</FormHelperText>
				</Box>
			)}
		</>
	);
};

const useStyles = makeStyles((theme) =>
	createStyles({
		emptyText: {
			lineHeight: 1,
		},
		menuPaper: {
			maxWidth: 536,
			width: `calc(100% - ${theme.spacing(4)})`,
		},
		menuItem: {
			whiteSpace: 'break-spaces',
		},
		menuItemCheckbox: {
			padding: 0,
		},
	}),
);

export default PermissionsSelect;
